import type { KeyboardEvent } from 'react';

import type { BodyAspect, Region } from './VisualDxDiagnosticFlow.constants';
import { silhouetteRegionClass } from './VisualDxDiagnosticFlow.styles';

/**
 * Clickable body silhouette — visual companion to the region grid in
 * step 1 of the VisualDx diagnostic flow.
 *
 * Spike-level component: the silhouette is composed of simple SVG
 * primitives (rounded rectangles + ellipses) rather than hand-drawn
 * paths so the spike can ship in a few hours and the shape is easy to
 * iterate on. A future polish pass would likely replace this with a
 * designer-provided SVG.
 *
 * Region IDs map to the existing ``Region`` discriminator in
 * ``VisualDxDiagnosticFlow.constants.ts`` so the silhouette and grid
 * share the same selection state without any translation layer.
 *
 * Accessibility:
 *  - Each region is a focusable element with ``role="button"``, an
 *    ``aria-label`` matching the grid label, and ``aria-pressed`` so
 *    screen readers expose the selected state.
 *  - Enter and Space toggle the region (mirrors native button behaviour).
 *  - The silhouette is a *companion*, not the only input — the grid
 *    remains accessible to keyboard- and screen-reader-only users, so
 *    the silhouette's slightly looser keyboard model (no arrow-key
 *    navigation between regions) is acceptable for the spike.
 *  - Silhouette aria-labels are suffixed " on silhouette" so they are
 *    unique vs. the grid's plain-text button labels (also keeps test
 *    queries unambiguous).
 *
 * The ``other`` region has no body coordinate and is intentionally
 * grid-only.
 */

interface BodySilhouetteProps {
  bodyAspect: BodyAspect;
  selectedRegions: Set<Region>;
  onToggleRegion: (region: Region) => void;
}

/** SVG viewBox used for both front and back views. */
const VIEW_BOX = '0 0 200 400';
const SVG_WIDTH = 180;
const SVG_HEIGHT = 360;

/** Geometry shared between front + back so the silhouette stays symmetric. */
const G = {
  head: { cx: 100, cy: 50, r: 30 },
  neck: { x: 88, y: 78, w: 24, h: 14, rx: 5 },
  chest: { x: 56, y: 92, w: 88, h: 60, rx: 10 },
  trunk: { x: 60, y: 152, w: 80, h: 70, rx: 10 },
  genitals: { cx: 100, cy: 222, rx: 16, ry: 11 },
  leftArm: { x: 38, y: 96, w: 18, h: 70, rx: 9 },
  rightArm: { x: 144, y: 96, w: 18, h: 70, rx: 9 },
  leftForearm: { x: 36, y: 168, w: 18, h: 66, rx: 9 },
  rightForearm: { x: 146, y: 168, w: 18, h: 66, rx: 9 },
  leftHand: { cx: 45, cy: 248, rx: 11, ry: 14 },
  rightHand: { cx: 155, cy: 248, rx: 11, ry: 14 },
  leftLeg: { x: 64, y: 222, w: 30, h: 130, rx: 10 },
  rightLeg: { x: 106, y: 222, w: 30, h: 130, rx: 10 },
  leftFoot: { cx: 79, cy: 358, rx: 14, ry: 10 },
  rightFoot: { cx: 121, cy: 358, rx: 14, ry: 10 },
} as const;

const interactiveProps = (
  region: Region,
  label: string,
  selected: boolean,
  onToggle: (r: Region) => void
) => {
  const handleKeyDown = (event: KeyboardEvent<SVGElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(region);
    }
  };
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': `${label} on silhouette`,
    'aria-pressed': selected,
    onClick: () => onToggle(region),
    onKeyDown: handleKeyDown,
    className: silhouetteRegionClass(selected),
  };
};

/** Decorative facial-feature hint on the front-view face. SVG attributes
 *  (no inline color hex) — `fill="none"` and `stroke` from the design
 *  system's `border` token via the `stroke-border` Tailwind utility. */
const facialHairClass = 'pointer-events-none fill-none stroke-border [stroke-width:0.8]';

interface RegionShapeProps {
  region: Region;
  label: string;
  selected: boolean;
  onToggle: (r: Region) => void;
}

const RegionRect = ({
  region,
  label,
  selected,
  onToggle,
  ...rect
}: RegionShapeProps & {
  x: number;
  y: number;
  w: number;
  h: number;
  rx: number;
}) => (
  <rect
    {...interactiveProps(region, label, selected, onToggle)}
    x={rect.x}
    y={rect.y}
    width={rect.w}
    height={rect.h}
    rx={rect.rx}
    ry={rect.rx}
  />
);

const RegionEllipse = ({
  region,
  label,
  selected,
  onToggle,
  ...el
}: RegionShapeProps & { cx: number; cy: number; rx: number; ry: number }) => (
  <ellipse
    {...interactiveProps(region, label, selected, onToggle)}
    cx={el.cx}
    cy={el.cy}
    rx={el.rx}
    ry={el.ry}
  />
);

export const BodySilhouette = ({
  bodyAspect,
  selectedRegions,
  onToggleRegion,
}: BodySilhouetteProps) => {
  const isSelected = (region: Region) => selectedRegions.has(region);
  const isBack = bodyAspect === 'back';

  const sharedRegions = (
    <>
      <RegionRect
        region="neck"
        label={isBack ? 'Neck (back)' : 'Neck'}
        selected={isSelected('neck')}
        onToggle={onToggleRegion}
        {...G.neck}
      />
      <RegionRect
        region="chest"
        label={isBack ? 'Chest (upper back)' : 'Chest'}
        selected={isSelected('chest')}
        onToggle={onToggleRegion}
        {...G.chest}
      />
      <RegionRect
        region="trunk"
        label={isBack ? 'Trunk (lower back)' : 'Trunk'}
        selected={isSelected('trunk')}
        onToggle={onToggleRegion}
        {...G.trunk}
      />
      <RegionEllipse
        region="genitals"
        label={isBack ? 'Genitals (buttocks)' : 'Genitals'}
        selected={isSelected('genitals')}
        onToggle={onToggleRegion}
        {...G.genitals}
      />
      <RegionRect
        region="arm"
        label={isBack ? 'Arm (back, left)' : 'Arm (left)'}
        selected={isSelected('arm')}
        onToggle={onToggleRegion}
        {...G.leftArm}
      />
      <RegionRect
        region="arm"
        label={isBack ? 'Arm (back, right)' : 'Arm (right)'}
        selected={isSelected('arm')}
        onToggle={onToggleRegion}
        {...G.rightArm}
      />
      <RegionRect
        region="forearm"
        label={isBack ? 'Forearm (back, left)' : 'Forearm (left)'}
        selected={isSelected('forearm')}
        onToggle={onToggleRegion}
        {...G.leftForearm}
      />
      <RegionRect
        region="forearm"
        label={isBack ? 'Forearm (back, right)' : 'Forearm (right)'}
        selected={isSelected('forearm')}
        onToggle={onToggleRegion}
        {...G.rightForearm}
      />
      <RegionEllipse
        region="hand"
        label={isBack ? 'Hand (back, left)' : 'Hand (left)'}
        selected={isSelected('hand')}
        onToggle={onToggleRegion}
        {...G.leftHand}
      />
      <RegionEllipse
        region="hand"
        label={isBack ? 'Hand (back, right)' : 'Hand (right)'}
        selected={isSelected('hand')}
        onToggle={onToggleRegion}
        {...G.rightHand}
      />
      <RegionRect
        region="leg"
        label={isBack ? 'Leg (back, left)' : 'Leg (left)'}
        selected={isSelected('leg')}
        onToggle={onToggleRegion}
        {...G.leftLeg}
      />
      <RegionRect
        region="leg"
        label={isBack ? 'Leg (back, right)' : 'Leg (right)'}
        selected={isSelected('leg')}
        onToggle={onToggleRegion}
        {...G.rightLeg}
      />
      <RegionEllipse
        region="foot"
        label={isBack ? 'Foot (heel, left)' : 'Foot (left)'}
        selected={isSelected('foot')}
        onToggle={onToggleRegion}
        {...G.leftFoot}
      />
      <RegionEllipse
        region="foot"
        label={isBack ? 'Foot (heel, right)' : 'Foot (right)'}
        selected={isSelected('foot')}
        onToggle={onToggleRegion}
        {...G.rightFoot}
      />
    </>
  );

  return (
    <svg
      viewBox={VIEW_BOX}
      width={SVG_WIDTH}
      height={SVG_HEIGHT}
      role="img"
      aria-label={
        isBack
          ? 'Back-view body silhouette. Tap a region to toggle.'
          : 'Front-view body silhouette. Tap a region to toggle.'
      }
    >
      {bodyAspect === 'front' ? (
        <>
          {/* Head: scalp = upper hemisphere, face = lower hemisphere. */}
          <path
            {...interactiveProps('scalp', 'Scalp', isSelected('scalp'), onToggleRegion)}
            d={`M ${G.head.cx - G.head.r} ${G.head.cy} A ${G.head.r} ${
              G.head.r
            } 0 0 1 ${G.head.cx + G.head.r} ${G.head.cy} L ${
              G.head.cx + G.head.r
            } ${G.head.cy} Z`}
          />
          <path
            {...interactiveProps('face', 'Face', isSelected('face'), onToggleRegion)}
            d={`M ${G.head.cx - G.head.r} ${G.head.cy} A ${G.head.r} ${
              G.head.r
            } 0 0 0 ${G.head.cx + G.head.r} ${G.head.cy} L ${
              G.head.cx + G.head.r
            } ${G.head.cy} Z`}
          />
          {/* Tiny non-interactive smile arc so the front view reads as a face. */}
          <path
            d={`M ${G.head.cx - 8} ${G.head.cy + 12} Q ${G.head.cx} ${
              G.head.cy + 16
            } ${G.head.cx + 8} ${G.head.cy + 12}`}
            className={facialHairClass}
          />
        </>
      ) : (
        <RegionEllipse
          region="scalp"
          label="Scalp (back of head)"
          selected={isSelected('scalp')}
          onToggle={onToggleRegion}
          cx={G.head.cx}
          cy={G.head.cy}
          rx={G.head.r}
          ry={G.head.r}
        />
      )}
      {sharedRegions}
    </svg>
  );
};
